/**
 * WHAT: PRO dashboard showing data classification KPIs, sensitivity distribution, and top classifications.
 * WHY: Gives administrators a single-page overview of their data governance posture --
 *      source counts, classification coverage, and sensitivity breakdown -- so gaps
 *      are immediately visible.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_CONFIG from '../../config/api.config';

/**
 * WHAT: Dashboard component with summary cards, sensitivity progress bars, and classification table.
 * WHY: Serves as the PRO module landing page; aggregated metrics help admins prioritise
 *      which data sources still need classification or sensitivity review.
 */
const ProDashboard = () => {
    const [summary, setSummary] = useState(null);
    const [classifications, setClassifications] = useState([]);
    const [sensitivity, setSensitivity] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    /* WHAT: Fetches summary, classification, and sensitivity data in parallel.
     * WHY: Three independent API calls are fired concurrently to minimise dashboard
     *      load time; all three datasets are needed for the complete dashboard view. */
    const fetchDashboardData = async () => {
        setLoading(true);
        setError(null);

        try {
            const [summaryRes, classRes, sensRes] = await Promise.all([
                axios.get(`${API_CONFIG.BASE_URL}/api/pro/dashboard/summary/`),
                axios.get(`${API_CONFIG.BASE_URL}/api/pro/dashboard/classifications/`),
                axios.get(`${API_CONFIG.BASE_URL}/api/pro/dashboard/sensitivity/`)
            ]);

            setSummary(summaryRes.data);
            setClassifications(classRes.data.classifications || []);
            setSensitivity(sensRes.data.sensitivity_levels || []);
        } catch (err) {
            console.error('Dashboard load error:', err);
            setError('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    /* WHAT: Maps a sensitivity level to its display color hex code.
     * WHY: Consistent color mapping (red=CRITICAL through green=LOW) across progress
     *      bars and badges ensures visual continuity with the rest of the PRO module. */
    const getSensitivityColor = (level) => {
        const colors = {
            'CRITICAL': '#dc3545',
            'HIGH': '#fd7e14',
            'MEDIUM': '#ffc107',
            'LOW': '#28a745',
            'UNCLASSIFIED': '#6c757d'
        };
        return colors[level] || '#6c757d';
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading dashboard...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger">
                <i className="fas fa-exclamation-circle me-2"></i>
                {error}
                <button className="btn btn-sm btn-outline-danger ms-3" onClick={fetchDashboardData}>
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div>
            {/* Summary Cards */}
            <div className="row g-3 mb-4">
                <div className="col-md-3">
                    <div className="card bg-primary text-white h-100">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 className="card-subtitle mb-2 opacity-75">Data Sources</h6>
                                    <h2 className="card-title mb-0">{summary?.total_sources || 0}</h2>
                                </div>
                                <i className="fas fa-database fa-2x opacity-50"></i>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-md-3">
                    <div className="card bg-info text-white h-100">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 className="card-subtitle mb-2 opacity-75">Data Assets</h6>
                                    <h2 className="card-title mb-0">{summary?.total_assets || 0}</h2>
                                </div>
                                <i className="fas fa-table fa-2x opacity-50"></i>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-md-3">
                    <div className="card bg-success text-white h-100">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 className="card-subtitle mb-2 opacity-75">Classified Attributes</h6>
                                    <h2 className="card-title mb-0">{summary?.classified_attributes || 0}</h2>
                                    <small className="opacity-75">
                                        of {summary?.total_attributes || 0} total
                                    </small>
                                </div>
                                <i className="fas fa-tags fa-2x opacity-50"></i>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-md-3">
                    <div className="card bg-warning text-dark h-100">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 className="card-subtitle mb-2 opacity-75">Users Mapped</h6>
                                    <h2 className="card-title mb-0">{summary?.total_users_mapped || 0}</h2>
                                </div>
                                <i className="fas fa-users fa-2x opacity-50"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Second Row */}
            <div className="row g-3 mb-4">
                <div className="col-md-4">
                    <div className="card h-100">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 className="card-subtitle text-muted mb-2">Targeted Scopes</h6>
                                    <h3 className="card-title mb-0">{summary?.total_scopes || 0}</h3>
                                </div>
                                <i className="fas fa-crosshairs fa-2x text-primary opacity-50"></i>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-md-4">
                    <div className="card h-100">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 className="card-subtitle text-muted mb-2">MDM Connections</h6>
                                    <h3 className="card-title mb-0">{summary?.active_mdm_connections || 0}</h3>
                                </div>
                                <i className="fas fa-plug fa-2x text-success opacity-50"></i>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-md-4">
                    <div className="card h-100">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 className="card-subtitle text-muted mb-2">Classification Types</h6>
                                    <h3 className="card-title mb-0">{summary?.total_classifications || 0}</h3>
                                </div>
                                <i className="fas fa-layer-group fa-2x text-info opacity-50"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4">
                {/* Sensitivity Distribution */}
                <div className="col-md-6">
                    <div className="card h-100">
                        <div className="card-header bg-secondary text-white">
                            <h5 className="mb-0">
                                <i className="fas fa-chart-pie me-2"></i>
                                Sensitivity Distribution
                            </h5>
                        </div>
                        <div className="card-body">
                            {sensitivity.length === 0 ? (
                                <p className="text-muted text-center py-4">
                                    No classified data yet
                                </p>
                            ) : (
                                <div>
                                    {sensitivity.map((item, idx) => (
                                        <div key={idx} className="mb-3">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <span>
                                                    <span
                                                        className="badge me-2"
                                                        style={{ backgroundColor: getSensitivityColor(item.sensitivity_level) }}
                                                    >
                                                        {item.sensitivity_level || 'UNCLASSIFIED'}
                                                    </span>
                                                </span>
                                                <span className="text-muted">
                                                    {item.attribute_count} attributes ({item.asset_count} assets)
                                                </span>
                                            </div>
                                            <div className="progress" style={{ height: '8px' }}>
                                                <div
                                                    className="progress-bar"
                                                    role="progressbar"
                                                    style={{
                                                        width: `${Math.min(100, (item.attribute_count / (summary?.total_attributes || 1)) * 100)}%`,
                                                        backgroundColor: getSensitivityColor(item.sensitivity_level)
                                                    }}
                                                ></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Top Classifications */}
                <div className="col-md-6">
                    <div className="card h-100">
                        <div className="card-header bg-secondary text-white">
                            <h5 className="mb-0">
                                <i className="fas fa-tags me-2"></i>
                                Top Classifications
                            </h5>
                        </div>
                        <div className="card-body p-0">
                            {classifications.length === 0 ? (
                                <p className="text-muted text-center py-4">
                                    No classifications found
                                </p>
                            ) : (
                                <table className="table table-hover mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Classification</th>
                                            <th>Category</th>
                                            <th className="text-end">Attributes</th>
                                            <th className="text-end">Assets</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {classifications.slice(0, 10).map((c, idx) => (
                                            <tr key={idx}>
                                                <td>
                                                    <span
                                                        className="badge me-2"
                                                        style={{ backgroundColor: c.color || '#6c757d' }}
                                                    >
                                                        {c.classification_code}
                                                    </span>
                                                    <small className="text-muted">{c.classification_name}</small>
                                                </td>
                                                <td>
                                                    <small>{c.category}</small>
                                                </td>
                                                <td className="text-end">{c.attribute_count || 0}</td>
                                                <td className="text-end">{c.asset_count || 0}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProDashboard;
